import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Plus, Trash2, GraduationCap, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const Wholesale = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ organization: "", contact_name: "", email: "", phone: "", notes: "" });
  const [rows, setRows] = useState([{ product_id: "", quantity: 24 }]);
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const items = rows.filter((r) => r.product_id && r.quantity > 0).map((r) => ({ ...r, bulk: true }));
      if (items.length === 0) throw new Error("Add at least one product");
      const { data } = await api.post("/wholesale/quote", { ...form, items });
      setSubmitted(data);
      toast.success("Quote generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
        <h1 className="font-display text-3xl font-extrabold mt-4">Quote #{submitted.quote_id}</h1>
        <p className="text-slate-600 mt-2">We'll email your finalized quote within 1 business day.</p>
        <div className="mt-6 p-6 bg-white border border-slate-200 rounded-2xl text-left">
          {submitted.items.map((it, i) => (
            <div key={i} className="flex justify-between py-2 border-b last:border-0 border-slate-100">
              <div>
                <div className="font-semibold">{it.name}</div>
                <div className="text-xs text-slate-500">{it.quantity} × ${it.unit_price.toFixed(2)}</div>
              </div>
              <div className="font-display font-bold">${(it.unit_price * it.quantity).toFixed(2)}</div>
            </div>
          ))}
          <div className="flex justify-between mt-4 pt-4 border-t border-slate-200">
            <div className="font-display font-extrabold">Estimated subtotal</div>
            <div className="font-display font-extrabold text-lg">${submitted.subtotal.toFixed(2)}</div>
          </div>
        </div>
        <Button className="mt-6 rounded-full bg-indigo-600 hover:bg-indigo-700" onClick={() => { setSubmitted(null); setRows([{ product_id: "", quantity: 24 }]); }}>New quote</Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-10 p-8 bg-gradient-to-br from-indigo-600 to-purple-700 text-white rounded-3xl">
        <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-xs font-semibold mb-4">
          <GraduationCap className="w-3 h-3" /> For schools, teachers & institutions
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Wholesale & bulk orders</h1>
        <p className="mt-2 text-white/85 max-w-2xl">Save up to 40% on bulk supplies. Perfect for classrooms, after-school programs, and district-wide procurement. Every order is auto-fulfilled through our wholesaler network.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 space-y-4 p-6 bg-white rounded-2xl border border-slate-200">
          <h2 className="font-display font-bold text-xl">Request a bulk quote</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Organization / School</Label><Input required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="wholesale-org-input" /></div>
            <div><Label>Contact name</Label><Input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="wholesale-name-input" /></div>
            <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wholesale-email-input" /></div>
            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="wholesale-phone-input" /></div>
          </div>

          <div>
            <Label>Products (wholesale pricing applied)</Label>
            <div className="space-y-2 mt-2">
              {rows.map((r, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select value={r.product_id} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, product_id: e.target.value } : x))}
                          className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white" data-testid={`wholesale-product-${i}`}>
                    <option value="">Select a product…</option>
                    {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} — ${p.wholesale_price.toFixed(2)} bulk</option>)}
                  </select>
                  <Input type="number" min={1} className="w-24" value={r.quantity} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, quantity: parseInt(e.target.value || "0") } : x))} data-testid={`wholesale-qty-${i}`} />
                  <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setRows([...rows, { product_id: "", quantity: 24 }])} className="mt-2 rounded-full" data-testid="wholesale-add-row"><Plus className="w-3 h-3 mr-1" />Add product</Button>
          </div>

          <div><Label>Additional notes (delivery, PO number, etc.)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="wholesale-notes" /></div>

          <Button type="submit" disabled={busy} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-bulk-order-form-submit">
            <Send className="w-4 h-4 mr-2" />{busy ? "Generating…" : "Get instant quote"}
          </Button>
        </form>

        <div className="space-y-4">
          <div className="p-6 bg-white rounded-2xl border border-slate-200">
            <div className="font-display font-bold text-lg">Bulk pricing tiers</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span>12–49 units</span><span className="font-semibold text-emerald-700">-25%</span></div>
              <div className="flex justify-between"><span>50–199 units</span><span className="font-semibold text-emerald-700">-32%</span></div>
              <div className="flex justify-between"><span>200+ units</span><span className="font-semibold text-emerald-700">-40%</span></div>
            </div>
          </div>
          <div className="p-6 bg-amber-50 border border-amber-200 rounded-2xl text-sm">
            <div className="font-semibold text-amber-900">Tax-exempt?</div>
            <div className="text-amber-800 mt-1">Include your Tax ID / exemption certificate in the notes and we'll apply it automatically.</div>
          </div>
        </div>
      </div>
    </div>
  );
};
